import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useLoginMutation } from "../../api/authApi";
import { useAppDispatch } from "../../hooks/redux";
import {
  loginStart,
  loginSuccess,
  loginFailure,
} from "../../store/slices/authSlice";
import flowLogo from "../../assets/flow_logo.svg";
import GoogleSignInButton from "./GoogleSignInButton";

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 50%, #ffb6b6 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Logo = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
`;

const Title = styled.h1`
  text-align: center;
  margin: 0;
  color: #333;
  font-size: 28px;
  font-weight: 600;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #555;
  font-weight: 500;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const Button = styled.button<{ loading?: boolean }>`
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
  color: white;
  border: none;
  padding: 14px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  opacity: ${(props) => (props.loading ? 0.7 : 1)};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 107, 107, 0.3);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c53030;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #c53030;
  font-size: 14px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
  color: #999;
  font-size: 14px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e1e5e9;
  }

  &::before {
    margin-right: 16px;
  }

  &::after {
    margin-left: 16px;
  }
`;

interface FormData {
  email: string;
  password: string;
}

const LoginForm: React.FC = () => {
  const isDevelopment = process.env.NODE_ENV === "development";

  const [formData, setFormData] = useState<FormData>({
    email: isDevelopment ? "admin@chalatech.com" : "",
    password: isDevelopment ? "admin123456" : "",
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const [login, { isLoading, error }] = useLoginMutation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isDevelopment) {
      setFormData({
        email: "admin@chalatech.com",
        password: "admin123456",
      });
    }
  }, [isDevelopment]);

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      dispatch(loginStart());
      const result = await login(formData).unwrap();

      if (result.success) {
        dispatch(
          loginSuccess({
            token: result.data.token,
            user: result.data.user,
          })
        );
      }
    } catch (err: any) {
      dispatch(loginFailure());
    }
  };

  const handleGoogleSignIn = () => {
    setIsGoogleLoading(true);
    const baseUrl =
      process.env.REACT_APP_API_URL || "http://localhost:8080/api";
    window.location.href = `${baseUrl}/auth/google`;
  };

  const getErrorMessage = () => {
    if (error) {
      if ("data" in error) {
        const errorData = error.data as any;
        return errorData?.error || "Login failed";
      }
      return "Network error. Please try again.";
    }
    return null;
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoContainer>
          <Logo src={flowLogo} alt="Flow Logo" />
          <Title>Welcome to Flow</Title>
        </LogoContainer>
        <GoogleSignInButton
          onSignIn={handleGoogleSignIn}
          loading={isGoogleLoading}
          disabled={isLoading}
        />

        <Divider>or</Divider>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading || isGoogleLoading}
              placeholder={
                isDevelopment
                  ? "admin@chalatech.com (dev default)"
                  : "Enter your email"
              }
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading || isGoogleLoading}
              placeholder={
                isDevelopment
                  ? "admin123456 (dev default)"
                  : "Enter your password"
              }
            />
            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
          </FormGroup>

          {getErrorMessage() && (
            <ErrorMessage>{getErrorMessage()}</ErrorMessage>
          )}

          <Button
            type="submit"
            disabled={isLoading || isGoogleLoading}
            loading={isLoading}
          >
            {isLoading && <LoadingSpinner />}
            {isLoading ? "Signing In..." : "Sign In"}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginForm;
